import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  ChevronLeft, 
  ChevronRight, 
  ChevronUp,
  ChevronDown,
  Save, 
  X, 
  AlertCircle,
  RefreshCw,
  Calendar,
  Plus,
  Trash2,
  AlertTriangle,
  TrendingUp,
  GripVertical
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { formatCurrencyInput, parseCurrencyInput } from "../utils/currencyFormat";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "../components/ui/alert";

import LastUpdated from "../components/LastUpdated";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const RevenueForecastPage = () => {
  const { yearParam } = useParams();
  const navigate = useNavigate();
  
  const currentYear = new Date().getFullYear();
  
  const [year, setYear] = useState(() => {
    if (yearParam) {
      const parsed = parseInt(yearParam);
      if (!isNaN(parsed) && parsed >= 2000 && parsed <= 2100) {
        return parsed;
      }
    }
    return currentYear;
  });
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [originalEntries, setOriginalEntries] = useState([]);
  const [entries, setEntries] = useState([]);
  const [deletedIds, setDeletedIds] = useState([]);

  useEffect(() => { document.title = "Revenue Forecast - Floatly"; }, []);

  // Update URL when year changes
  useEffect(() => {
    const newPath = `/revenue-forecast/${year}`;
    if (window.location.pathname !== newPath) {
      navigate(newPath, { replace: true });
    }
  }, [year, navigate]);

  // Fetch data for year
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setDeletedIds([]);
    try {
      const response = await axios.get(`${API}/revenue-forecast/${year}`);
      const fetchedEntries = response.data.entries;
      
      setOriginalEntries(JSON.parse(JSON.stringify(fetchedEntries)));
      setEntries(fetchedEntries);
    } catch (err) {
      console.error("Error fetching revenue forecast:", err);
      setError("Failed to load revenue forecast data");
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Check if there are changes
  const hasChanges = useMemo(() => {
    if (deletedIds.length > 0) return true;
    return JSON.stringify(entries) !== JSON.stringify(originalEntries);
  }, [entries, originalEntries, deletedIds]);

  // Navigation
  const goToPrevYear = () => {
    if (hasChanges && !window.confirm("You have unsaved changes. Discard and navigate?")) {
      return;
    }
    setYear(y => y - 1);
  };

  const goToNextYear = () => {
    if (hasChanges && !window.confirm("You have unsaved changes. Discard and navigate?")) {
      return;
    }
    setYear(y => y + 1);
  };

  const goToCurrentYear = () => {
    if (hasChanges && !window.confirm("You have unsaved changes. Discard and navigate?")) {
      return;
    }
    setYear(currentYear);
  };

  // Get last day of month
  const getLastDayOfMonth = (yearNum, monthNum) => {
    const nextMonth = new Date(yearNum, monthNum, 1);
    const lastDay = new Date(nextMonth - 1);
    return lastDay.toISOString().split('T')[0];
  };

  // Handle entry changes
  const handleEntryChange = (index, field, value) => {
    setEntries(prev => {
      const newEntries = [...prev];
      newEntries[index] = { ...newEntries[index], [field]: value };
      
      // If month changes and it's a valid YYYY-MM format, update receipt_date to last day of new month
      if (field === "month" && value && value.match(/^\d{4}-\d{2}$/)) {
        const [y, m] = value.split("-").map(Number);
        newEntries[index].receipt_date = getLastDayOfMonth(y, m);
      }
      
      return newEntries;
    });
  };

  // Add new entry
  const handleAddEntry = (monthStr = null) => {
    const defaultMonth = monthStr || `${year}-01`;
    const [y, m] = defaultMonth.split("-").map(Number);
    
    // New entries get added at the end with highest sort_order
    const maxSortOrder = entries.reduce((max, e) => Math.max(max, e.sort_order || 0), -1);
    
    const newEntry = {
      id: null, // null id indicates new entry
      month: defaultMonth,
      receipt_date: getLastDayOfMonth(y, m),
      expected_net_revenue_inc_vat: 0,
      sort_order: maxSortOrder + 1,
      _isNew: true
    };
    
    setEntries(prev => [...prev, newEntry]);
  };

  // Move entry up
  const handleMoveUp = (index) => {
    if (index <= 0) return;
    
    setEntries(prev => {
      const newEntries = [...prev];
      // Swap sort_order values
      const currentOrder = newEntries[index].sort_order;
      const prevOrder = newEntries[index - 1].sort_order;
      newEntries[index] = { ...newEntries[index], sort_order: prevOrder };
      newEntries[index - 1] = { ...newEntries[index - 1], sort_order: currentOrder };
      // Swap positions in array
      [newEntries[index], newEntries[index - 1]] = [newEntries[index - 1], newEntries[index]];
      return newEntries;
    });
  };

  // Move entry down
  const handleMoveDown = (index) => {
    if (index >= entries.length - 1) return;
    
    setEntries(prev => {
      const newEntries = [...prev];
      // Swap sort_order values
      const currentOrder = newEntries[index].sort_order;
      const nextOrder = newEntries[index + 1].sort_order;
      newEntries[index] = { ...newEntries[index], sort_order: nextOrder };
      newEntries[index + 1] = { ...newEntries[index + 1], sort_order: currentOrder };
      // Swap positions in array
      [newEntries[index], newEntries[index + 1]] = [newEntries[index + 1], newEntries[index]];
      return newEntries;
    });
  };

  // Delete entry
  const handleDeleteEntry = (index) => {
    const entry = entries[index];
    
    // If entry has an id (exists in DB), mark for deletion
    if (entry.id && !entry._isNew) {
      setDeletedIds(prev => [...prev, entry.id]);
    }
    
    setEntries(prev => prev.filter((_, i) => i !== index));
  };

  // Save all changes
  const handleSave = async () => {
    if (!hasChanges) return;
    
    // Validate entries
    for (const entry of entries) {
      const amount = parseFloat(entry.expected_net_revenue_inc_vat) || 0;
      if (amount < 0 || amount > 9999999.99) {
        toast.error("Invalid amount. Must be between €0 and €9,999,999.99");
        return;
      }
      if (!entry.month) {
        toast.error("All entries must have a month/description");
        return;
      }
    }
    
    setSaving(true);
    try {
      // Prepare bulk update payload
      const bulkEntries = [
        // Regular entries (create/update) - include sort_order based on current position
        ...entries.map((e, index) => ({
          id: e._isNew ? null : e.id,
          month: e.month,
          receipt_date: e.receipt_date,
          expected_net_revenue_inc_vat: parseFloat(e.expected_net_revenue_inc_vat) || 0,
          sort_order: index
        })),
        // Deleted entries
        ...deletedIds.map(id => ({ id, _delete: true }))
      ];
      
      await axios.post(`${API}/revenue-forecast/bulk?year=${year}`, { entries: bulkEntries });
      
      // Refresh data after save
      await fetchData();
      toast.success("Revenue forecast saved successfully");
    } catch (err) {
      console.error("Error saving revenue forecast:", err);
      toast.error("Failed to save revenue forecast");
    } finally {
      setSaving(false);
    }
  };

  // Discard changes
  const handleDiscard = () => {
    setEntries(JSON.parse(JSON.stringify(originalEntries)));
    setDeletedIds([]);
    toast.info("Changes discarded");
  };

  // Format currency
  const formatCurrency = (value) => {
    const num = parseFloat(value) || 0;
    return new Intl.NumberFormat("nl-NL", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  // Calculate totals
  const totalRevenue = useMemo(() => {
    return entries.reduce((sum, e) => sum + (parseFloat(e.expected_net_revenue_inc_vat) || 0), 0);
  }, [entries]);

  // Calculate monthly totals
  const monthlyTotals = useMemo(() => {
    const totals = {};
    entries.forEach(e => {
      if (!totals[e.month]) totals[e.month] = 0;
      totals[e.month] += parseFloat(e.expected_net_revenue_inc_vat) || 0;
    });
    return totals;
  }, [entries]);

  // Format month for display
  const formatMonth = (monthStr) => {
    const [y, m] = monthStr.split("-");
    return `${MONTHS[parseInt(m) - 1]} ${y}`;
  };

  // Generate month options for the year
  const monthOptions = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => ({
      value: `${year}-${String(i + 1).padStart(2, '0')}`,
      label: `${MONTHS[i]} ${year}`
    }));
  }, [year]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" data-testid="error-state">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-destructive" />
          <h2 className="text-2xl font-semibold mb-2">Something went wrong</h2>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Button onClick={fetchData} data-testid="retry-button">
            <RefreshCw className="w-4 h-4 mr-2" />
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" data-testid="revenue-forecast-page">
      {/* Header */}
      <header className="border-b bg-white shrink-0">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="page-title">
                Revenue Forecast
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Expected net revenue by receipt date (incl. VAT)
              </p>
              <LastUpdated page="revenue-forecast" />
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Year Total</p>
              <p className="text-2xl font-semibold text-green-600" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                {formatCurrency(totalRevenue)}
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Year Navigator */}
      <div className="border-b bg-white/80 backdrop-blur sticky top-0 z-30 shrink-0">
        <div className="px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={goToPrevYear}
                className="rounded-full"
                data-testid="prev-year-button"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              
              <h2 className="text-xl font-semibold min-w-[100px] text-center" data-testid="current-year">
                {year}
              </h2>
              
              <Button
                variant="ghost"
                size="icon"
                onClick={goToNextYear}
                className="rounded-full"
                data-testid="next-year-button"
              >
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="text-sm text-muted-foreground hidden md:block">
                {entries.length} entries • {Object.keys(monthlyTotals).length} months
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleAddEntry()}
                className="rounded-full"
                data-testid="add-entry-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Receipt
              </Button>
              {year !== currentYear && (
                <Button
                  variant="outline"
                  onClick={goToCurrentYear}
                  className="rounded-full"
                  data-testid="current-year-button"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  {currentYear}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Warning Banner */}
      <div className="px-6 pt-4">
        <Alert className="bg-amber-50 border-amber-200" data-testid="warning-banner">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-800 font-semibold">Important: Avoid Double-Counting</AlertTitle>
          <AlertDescription className="text-amber-700">
            Enter only <strong>expected future revenue</strong> here. Do not include amounts from sales invoices 
            that have already been sent — those are tracked separately and will cause double-counting in the cashflow forecast.
          </AlertDescription>
        </Alert>
      </div>

      {/* Main Content */}
      <main className="flex-1 overflow-auto px-6 py-4">
        {loading ? (
          <div className="flex items-center justify-center py-20" data-testid="loading-state">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : entries.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="empty-state">
            <TrendingUp className="w-12 h-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No revenue forecast entries</h3>
            <p className="text-muted-foreground mb-4">Add your first expected revenue receipt to get started.</p>
            <Button onClick={() => handleAddEntry()} data-testid="add-first-entry">
              <Plus className="w-4 h-4 mr-2" />
              Add First Receipt
            </Button>
          </div>
        ) : (
          <div className="bg-white border rounded-xl shadow-sm overflow-auto" style={{ maxHeight: "calc(100vh - 280px)", paddingBottom: "40px" }}>
            <table className="w-full" data-testid="revenue-table">
              <thead className="bg-white border-b sticky top-0 z-10">
                <tr>
                  <th className="w-[70px] px-2 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider text-center">
                    Order
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider w-[180px]">
                    Month / Description
                  </th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider w-[160px]">
                    Receipt Date
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider w-[200px]">
                    Expected Net Revenue (incl. VAT)
                  </th>
                  <th className="w-[50px]"></th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry, index) => (
                  <tr 
                    key={entry.id || `new-${index}`}
                    className="border-b last:border-b-0 transition-colors hover:bg-muted/30"
                    data-testid={`row-${index}`}
                  >
                    <td className="px-2 py-2">
                      <div className="flex items-center justify-center gap-0.5">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleMoveUp(index)}
                          disabled={index === 0}
                          className="h-7 w-7 text-muted-foreground hover:text-foreground disabled:opacity-30"
                          data-testid={`move-up-${index}`}
                        >
                          <ChevronUp className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleMoveDown(index)}
                          disabled={index === entries.length - 1}
                          className="h-7 w-7 text-muted-foreground hover:text-foreground disabled:opacity-30"
                          data-testid={`move-down-${index}`}
                        >
                          <ChevronDown className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <Input
                        type="text"
                        list={`month-suggestions-${index}`}
                        value={entry.month.match(/^\d{4}-\d{2}$/) ? formatMonth(entry.month) : entry.month}
                        onChange={(e) => {
                          const inputVal = e.target.value;
                          // Check if it matches a month option
                          const matchedOption = monthOptions.find(opt => opt.label === inputVal);
                          if (matchedOption) {
                            handleEntryChange(index, "month", matchedOption.value);
                          } else {
                            handleEntryChange(index, "month", inputVal);
                          }
                        }}
                        className="h-9 text-sm"
                        placeholder="Select or type..."
                        data-testid={`month-input-${index}`}
                      />
                      <datalist id={`month-suggestions-${index}`}>
                        {monthOptions.map(opt => (
                          <option key={opt.value} value={opt.label} />
                        ))}
                      </datalist>
                    </td>
                    <td className="px-4 py-2">
                      <Input
                        type="date"
                        value={entry.receipt_date || ""}
                        onChange={(e) => handleEntryChange(index, "receipt_date", e.target.value)}
                        className="font-mono h-9 text-sm text-center"
                        data-testid={`date-input-${index}`}
                      />
                    </td>
                    <td className="px-4 py-2">
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">€</span>
                        <Input
                          type="text"
                          inputMode="decimal"
                          value={formatCurrencyInput(entry.expected_net_revenue_inc_vat)}
                          onChange={(e) => handleEntryChange(index, "expected_net_revenue_inc_vat", parseCurrencyInput(e.target.value))}
                          onFocus={(e) => {
                            const val = entry.expected_net_revenue_inc_vat || 0;
                            e.target.value = val !== 0 ? val.toString().replace(".", ",") : "";
                            e.target.select();
                          }}
                          onBlur={(e) => {
                            e.target.value = formatCurrencyInput(parseCurrencyInput(e.target.value));
                          }}
                          className="pl-7 text-right font-mono h-9 text-sm"
                          placeholder="0"
                          data-testid={`amount-input-${index}`}
                        />
                      </div>
                    </td>
                    <td className="px-2 py-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteEntry(index)}
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        data-testid={`delete-btn-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-muted/50 border-t-2">
                <tr>
                  <td className="px-4 py-3 font-semibold" colSpan={3}>
                    Year Total ({entries.length} entries)
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="font-mono font-bold text-lg text-green-600">
                      {formatCurrency(totalRevenue)}
                    </span>
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {/* Monthly Summary */}
        {!loading && entries.length > 0 && (
          <div className="mt-6 bg-white border rounded-xl shadow-sm p-4">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
              Monthly Summary
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {monthOptions.map(opt => {
                const total = monthlyTotals[opt.value] || 0;
                const entryCount = entries.filter(e => e.month === opt.value).length;
                
                return (
                  <div 
                    key={opt.value}
                    className={`p-3 rounded-lg border ${entryCount > 0 ? 'bg-green-50 border-green-200' : 'bg-muted/30 border-transparent'}`}
                  >
                    <div className="text-xs text-muted-foreground">{MONTHS[parseInt(opt.value.split('-')[1]) - 1]}</div>
                    <div className={`font-mono font-semibold ${total > 0 ? 'text-green-600' : 'text-muted-foreground'}`}>
                      {formatCurrency(total)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </main>

      {/* Floating Action Bar */}
      {hasChanges && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-white border rounded-full shadow-lg px-6 py-3 flex items-center gap-4 animate-in fade-in slide-in-from-bottom-4" data-testid="action-bar">
          <span className="text-sm text-muted-foreground">
            {deletedIds.length > 0 ? `${deletedIds.length} to delete • ` : ''}
            Unsaved changes
          </span>
          <Button variant="outline" onClick={handleDiscard} className="rounded-full" data-testid="discard-button">
            <X className="w-4 h-4 mr-2" />
            Discard
          </Button>
          <Button onClick={handleSave} disabled={saving} className="rounded-full bg-primary text-primary-foreground" data-testid="save-button">
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving..." : "Save Forecast"}
          </Button>
        </div>
      )}
    </div>
  );
};

export default RevenueForecastPage;
